/**
 * Shop BUY/Claim: hold 3s with center-expanding fill to confirm purchase.
 * Decorates the existing button in place (same layout as Owned).
 */
import {
  playShopHoldCancelSound,
  playShopHoldConfirmSound,
  startShopHoldChargeSound,
  type ShopHoldChargeHandle,
} from "./ui-sounds";

export const SHOP_HOLD_CONFIRM_MS = 3000;
const SHOP_HOLD_REVERSE_MS = 280;
/** Presses shorter than this count as impatient taps. */
const SHOP_HOLD_TAP_MS = 420;
/** How many rapid taps trigger the Hold Me! hint. */
const SHOP_HOLD_HINT_TAPS = 3;
/** Rapid taps must land within this window. */
const SHOP_HOLD_HINT_WINDOW_MS = 1600;
/** How long "Hold Me!" stays on the button. */
const SHOP_HOLD_HINT_SHOW_MS = 3000;
const SHOP_HOLD_HINT_LABEL = "Hold Me!";

type HoldState = {
  pointerId: number;
  startedAt: number;
  raf: number;
  charge: ShopHoldChargeHandle | null;
  reversing: boolean;
};

/**
 * Replace click-to-buy with a 3s hold-to-confirm gesture on the button itself.
 */
export function bindShopHoldConfirm(
  btn: HTMLButtonElement,
  onConfirm: () => void | Promise<void>,
): void {
  if (btn.disabled) return;

  btn.classList.add("shopHoldConfirmBtn");
  btn.style.setProperty("--hold-p", "0");

  // Keep label above the expanding fill.
  const labelText = (btn.textContent ?? "").trim() || "BUY";
  btn.textContent = "";
  const fill = document.createElement("span");
  fill.className = "shopHoldConfirmFill";
  fill.setAttribute("aria-hidden", "true");
  const label = document.createElement("span");
  label.className = "shopHoldConfirmLabel";
  label.textContent = labelText;
  btn.appendChild(fill);
  btn.appendChild(label);

  let state: HoldState | null = null;
  let busy = false;
  let tapTimes: number[] = [];
  let hintTimer = 0;
  let hintActive = false;

  const setProgress = (p: number) => {
    const clamped = Math.max(0, Math.min(1, p));
    btn.style.setProperty("--hold-p", String(clamped));
    btn.classList.toggle("shopHoldConfirmBtn--progress", clamped > 0.001);
  };

  const clearRaf = () => {
    if (state?.raf) cancelAnimationFrame(state.raf);
    if (state) state.raf = 0;
  };

  const stopCharge = (mode: "complete" | "cancel") => {
    state?.charge?.stop(mode);
    if (state) state.charge = null;
  };

  const clearHoldHint = () => {
    if (hintTimer) {
      window.clearTimeout(hintTimer);
      hintTimer = 0;
    }
    if (!hintActive) return;
    hintActive = false;
    label.textContent = labelText;
    btn.classList.remove("shopHoldConfirmBtn--holdHint");
    btn.removeAttribute("aria-live");
  };

  const showHoldHint = () => {
    tapTimes = [];
    if (hintTimer) window.clearTimeout(hintTimer);
    hintActive = true;
    label.textContent = SHOP_HOLD_HINT_LABEL;
    btn.classList.add("shopHoldConfirmBtn--holdHint");
    btn.setAttribute("aria-live", "polite");
    hintTimer = window.setTimeout(() => {
      hintTimer = 0;
      clearHoldHint();
    }, SHOP_HOLD_HINT_SHOW_MS);
  };

  const noteImpatientTap = (elapsed: number) => {
    if (elapsed > SHOP_HOLD_TAP_MS || busy) return;
    const now = performance.now();
    tapTimes = tapTimes.filter((t) => now - t <= SHOP_HOLD_HINT_WINDOW_MS);
    tapTimes.push(now);
    if (tapTimes.length >= SHOP_HOLD_HINT_TAPS) showHoldHint();
  };

  const reverseOut = (elapsed: number) => {
    if (!state) {
      setProgress(0);
      return;
    }
    state.reversing = true;
    const from = Math.max(0, Math.min(1, elapsed / SHOP_HOLD_CONFIRM_MS));
    stopCharge("cancel");
    playShopHoldCancelSound();
    clearRaf();
    noteImpatientTap(elapsed);
    const t0 = performance.now();
    const step = (now: number) => {
      if (!state?.reversing) return;
      const u = Math.min(1, (now - t0) / SHOP_HOLD_REVERSE_MS);
      const eased = 1 - Math.pow(1 - u, 2);
      setProgress(from * (1 - eased));
      if (u < 1) {
        state.raf = requestAnimationFrame(step);
      } else {
        setProgress(0);
        state = null;
        btn.classList.remove("shopHoldConfirmBtn--holding");
      }
    };
    state.raf = requestAnimationFrame(step);
  };

  const finishHold = () => {
    if (!state || state.reversing || busy) return;
    clearRaf();
    stopCharge("complete");
    playShopHoldConfirmSound();
    setProgress(1);
    state = null;
    busy = true;
    tapTimes = [];
    clearHoldHint();
    btn.classList.remove("shopHoldConfirmBtn--holding");
    btn.classList.add("shopHoldConfirmBtn--armed");
    void Promise.resolve(onConfirm())
      .catch(() => {})
      .finally(() => {
        busy = false;
        btn.classList.remove("shopHoldConfirmBtn--armed");
        const t0 = performance.now();
        const fade = (now: number) => {
          const u = Math.min(1, (now - t0) / 220);
          setProgress(1 - u);
          if (u < 1) requestAnimationFrame(fade);
          else setProgress(0);
        };
        requestAnimationFrame(fade);
      });
  };

  const tick = (now: number) => {
    if (!state || state.reversing) return;
    const p = (now - state.startedAt) / SHOP_HOLD_CONFIRM_MS;
    setProgress(p);
    if (p >= 1) {
      finishHold();
      return;
    }
    state.raf = requestAnimationFrame(tick);
  };

  const startHold = (e: PointerEvent) => {
    if (btn.disabled || busy || state) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      btn.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    state = {
      pointerId: e.pointerId,
      startedAt: performance.now(),
      raf: 0,
      charge: startShopHoldChargeSound(SHOP_HOLD_CONFIRM_MS),
      reversing: false,
    };
    btn.classList.add("shopHoldConfirmBtn--holding");
    setProgress(0);
    state.raf = requestAnimationFrame(tick);
  };

  const endHold = (e: PointerEvent) => {
    if (!state || state.pointerId !== e.pointerId) return;
    if (state.reversing) return;
    const elapsed = performance.now() - state.startedAt;
    try {
      btn.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (elapsed >= SHOP_HOLD_CONFIRM_MS - 16) {
      if (state) {
        clearRaf();
        stopCharge("complete");
        state = null;
      }
      btn.classList.remove("shopHoldConfirmBtn--holding");
      return;
    }
    reverseOut(elapsed);
  };

  btn.addEventListener("pointerdown", startHold);
  btn.addEventListener("pointerup", endHold);
  btn.addEventListener("pointercancel", endHold);
  btn.addEventListener("lostpointercapture", (e) => {
    if (state && e.pointerId === state.pointerId && !state.reversing && !busy) {
      const elapsed = performance.now() - state.startedAt;
      if (elapsed < SHOP_HOLD_CONFIRM_MS - 16) reverseOut(elapsed);
    }
  });
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
  });
}
